import { FormEvent, ReactElement, useMemo, useState } from "react";
import { Alert, Box, ButtonBase, SxProps, Theme, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DetailedRound } from "../../../models/round";
import { Coupon } from "../../../models/couponshares";
import { useSimplePurchase } from "../../../rounds/usePurchase";
import { usePlayer } from "../../../player/playerContext";
import { operatorAdviceBetSuccess, operatorAdviceLogin, operatorAdviceScrollToTop } from "../../../App";
import { trackBeginCheckout, trackCouponTool, trackPurchase, track } from "../../../analytics/analytics";
import { PoolXNumberFormat } from "../../../common/CurrencyFormatter";
import { couponTotal } from "../../../slimcoupon/couponhelpers";
import LoginDialog from "../../../slimcoupon/logindialog";
import ConfirmPayment, { ErrorResponse } from "../couponform/components/confirmpayment";
import ShowResultOfBet from "../couponform/components/showresultofbet";
import { IsLeo, IsOrangeDark, IsVideo } from "../../../theme/themehelper";
import TheGamePill from "./thegamepill";
import { marketSigns, TheGameManifest, toCouponSign } from "./thegamemanifest";
import { getMarketLabel, getSelectionLabel } from "./thegamemarkets";
import {
  cardSx,
  formatCountdown,
  getPot,
  LINE,
  mainSx,
  marketCardSx,
  pageSx,
  ProgressCells,
  sectionTitleSx,
  statValueSx,
  supportiveSx,
  tileBackground,
  rowShares,
  useCountdown,
} from "./thegamecommon";

const STAKE_MULTIPLIERS = [1, 2, 5, 10];

/* ------------------------------------------------------------------ */
/*  Sx styles                                                         */
/* ------------------------------------------------------------------ */

const ghostButtonSx: SxProps<Theme> = {
  height: 36,
  padding: "0 4px",
  gap: "6px",
  fontFamily: "var(--font-body)",
  fontSize: 15,
  fontWeight: 600,
  color: "var(--sign-color)",
};

const chipSx: SxProps<Theme> = {
  minWidth: 44,
  height: 36,
  padding: "0 12px",
  borderRadius: "18px",
  fontFamily: "var(--font-body)",
  fontSize: 15,
  fontWeight: 600,
  background: "var(--sign-background-not-selected)",
  color: "var(--sign-color)",
};

const chipOnSx: SxProps<Theme> = {
  background: "var(--sign-background)",
  color: "var(--sign-color-selected)",
};

const mainButtonSx: SxProps<Theme> = {
  width: "100%",
  height: 48,
  borderRadius: "var(--button-border-radius)",
  fontFamily: "var(--font-button, var(--font-body))",
  fontSize: 16,
  fontWeight: 500,
  textTransform: "uppercase",
  background: "var(--button-color)",
  color: "var(--button-text-color)",
  "&.Mui-disabled": {
    background: "var(--button-disabled)",
    color: "var(--button-disabled-text-color, #000)",
    textTransform: "none",
  },
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function emptyPicks(numMarkets: number): string[][] {
  return Array.from({ length: numMarkets }, () => []);
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                    */
/* ------------------------------------------------------------------ */

function HeaderCard({ round, manifest }: { round: DetailedRound; manifest: TheGameManifest }): ReactElement {
  const { t } = useTranslation();
  const openTo = useMemo(() => new Date(round.openTo), [round.openTo]);
  const timeLeft = useCountdown(openTo);

  const pot = getPot(round, t, manifest.markets.length);

  return (
    <Box
      component="section"
      sx={{ ...cardSx, background: tileBackground(), padding: "16px", display: "flex", flexDirection: "column", gap: "10px" } as SxProps<Theme>}
    >
      <Typography sx={{ fontFamily: "var(--font-title)", fontSize: 16, fontWeight: 700, textTransform: "uppercase", color: "var(--thegame-accent, inherit)" }}>
        {t("the_game")}
      </Typography>
      <Typography sx={{ fontFamily: "var(--font-title)", fontSize: 28, fontWeight: 700, lineHeight: "32px" }}>
        {manifest.match.home} – {manifest.match.away}
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px", borderTop: `1px solid ${LINE}`, paddingTop: "10px" }}>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography sx={supportiveSx}>{pot.label}</Typography>
          <Typography sx={{ ...statValueSx, fontSize: 20 } as SxProps<Theme>}>{pot.text}</Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
          <Typography sx={supportiveSx}>{t("closes_in")}</Typography>
          <Typography sx={{ ...statValueSx, fontSize: 20, fontVariantNumeric: "tabular-nums" } as SxProps<Theme>}>
            {formatCountdown(timeLeft)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function PickTag({ count }: { count: number }): ReactElement {
  const { t } = useTranslation();
  const isSystem = count >= 2;
  const text = count === 0 ? t("tg_no_pick") : count === 1 ? t("tg_one_pick") : t("tg_n_picks", { count });
  return (
    <Box
      component="span"
      sx={{
        fontSize: 13,
        fontWeight: 600,
        borderRadius: "12px",
        padding: "1px 8px",
        whiteSpace: "nowrap",
        border: `1px solid ${isSystem ? "var(--sign-background)" : LINE}`,
        color: isSystem ? "var(--sign-color-selected)" : "var(--font-color-supportive)",
        background: isSystem ? "var(--sign-background)" : "transparent",
      }}
    >
      {text}
    </Box>
  );
}

function QuickPickIcon(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <circle cx="8.5" cy="8.5" r="1.2" />
      <circle cx="15.5" cy="15.5" r="1.2" />
      <circle cx="15.5" cy="8.5" r="1.2" />
      <circle cx="8.5" cy="15.5" r="1.2" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                    */
/* ------------------------------------------------------------------ */

interface TheGameBetViewProps {
  round: DetailedRound;
  manifest: TheGameManifest;
}

export default function TheGameBetView({ round, manifest }: TheGameBetViewProps): ReactElement {
  const { t } = useTranslation();
  const [player] = usePlayer() as [any, any];
  const numLegs = round.fixtures.length;

  const [picks, setPicks] = useState<string[][]>(() => emptyPicks(numLegs));
  const [stakeMultiplier, setStakeMultiplier] = useState(1);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showResultOfBet, setShowResultOfBet] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [submitError, setSubmitError] = useState<ErrorResponse | undefined>(undefined);
  const purchase = useSimplePurchase(round, stakeMultiplier);

  const isAfterDeadline = new Date(round.openTo) < new Date();
  const isOpen = round.status === "ACTIVE" && !isAfterDeadline;
  const isRealMode = player?._embedded?.mode === "real";
  const showLogin = IsLeo(window.theme) || (IsVideo(window.theme) && player?._links?.self?.href?.indexOf("demo") > -1);

  const coupon: Coupon = {
    bets: [{ system: { type: "MATHEMATICAL" }, legs: picks.map((signs) => ({ signs: [...signs] })) }],
    stakePerRow: round.operatorStakePerRow,
    roundId: round.id,
  };

  const numPicked = picks.filter((signs) => signs.length > 0).length;
  const numMissing = numLegs - numPicked;
  const isComplete = numMissing === 0 && manifest.markets.length === numLegs;
  const numRows = picks.reduce((rows, signs) => rows * Math.max(signs.length, 1), 1);
  // Hide the bars until someone has bet, otherwise every pill would read 0%.
  const hasShares = round.fixtures.some((f) => Object.values(rowShares(f) ?? {}).some((share) => (share ?? 0) > 0));
  const total = couponTotal(round, coupon, stakeMultiplier);

  const currency = round.operatorStakePerRow.currency;
  const nf = PoolXNumberFormat(t("locale"), { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const rowPrice = (Number(round.operatorStakePerRow.amount) + Number(round.operatorFeePerRow?.amount ?? 0)) * stakeMultiplier;

  const togglePick = (pos: number, sign: string): void => {
    if (!isOpen) return;
    if (numPicked === 0) {
      track("coupon_started", { round_id: round.id, product: round.name });
    }
    setPicks((prev) =>
      prev.map((signs, i) => (i !== pos ? signs : signs.includes(sign) ? signs.filter((s) => s !== sign) : [...signs, sign]))
    );
  };

  const quickPick = (): void => {
    trackCouponTool("random_pick", { round_id: round.id });
    setPicks((prev) =>
      prev.map((signs, i) => {
        const market = manifest.markets.find((m) => m.pos === i);
        if (signs.length > 0 || !market) return signs;
        const available = marketSigns(market);
        return [toCouponSign(available[Math.floor(Math.random() * available.length)])];
      })
    );
  };

  const clearPicks = (): void => setPicks(emptyPicks(numLegs));

  const handleErrorMessageClose = (): void => {
    if (submitFailed) {
      setSubmitFailed(false);
      setSubmitError(undefined);
      setShowConfirm(false);
    }
  };

  const handlePayClick = (): void => {
    if (showLogin && !isRealMode) {
      setShowLoginDialog(true);
    } else {
      trackBeginCheckout({ round_id: round.id, value: Number(total), currency, rows: numRows, system_type: "MATHEMATICAL" });
      setShowConfirm(true);
    }
    operatorAdviceScrollToTop(500);
  };

  const handleLoginClick = (): void => {
    setShowLoginDialog(false);
    operatorAdviceLogin({ type: "login_user", navigationId: round.id, navigationKey: "round", roundId: round.id });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitting(true);
    const res = (await purchase(coupon)) as Response;
    const data = await res.json();
    setSubmitting(false);

    if (res.status !== 200) {
      setSubmitError(data);
      setSubmitFailed(true);
      return;
    }

    setSubmitFailed(false);
    setSubmitError(undefined);
    clearPicks();

    if (data && data.transactionId) {
      operatorAdviceBetSuccess({
        type: "bet_success",
        amount: data.amount,
        currency: data.currency,
        transactionId: data.transactionId,
        roundId: data.roundId,
      });
      setShowConfirm(false);
      setShowResultOfBet(true);
      trackPurchase(data, "the_game", "MATHEMATICAL", stakeMultiplier);
    }
  };

  const barShadow = IsOrangeDark(window.theme) ? "0 -4px 16px rgba(0, 0, 0, .5)" : "0 -4px 16px rgba(23, 49, 84, .12)";

  let mainButtonText = `${t("pay")} · ${nf.format(total)}`;
  if (numMissing > 0) {
    mainButtonText = numMissing === 1 ? t("pick_in_one_more_market") : t("pick_in_more_markets", { count: numMissing });
  }

  return (
    <Box sx={pageSx}>
      {showResultOfBet && <ShowResultOfBet toggle={setShowResultOfBet} />}
      {showLoginDialog && <LoginDialog open={showLoginDialog} onClose={handleLoginClick} />}

      <form onSubmit={handleSubmit}>
        {showConfirm && (
          <ConfirmPayment
            amount={total}
            currency={currency}
            toggle={setShowConfirm}
            submitting={submitting}
            submitFailed={submitFailed}
            submitError={submitError}
            stakePerRow={Number(round.operatorStakePerRow.amount)}
            handleCloseDialog={handleErrorMessageClose}
          />
        )}
      </form>

      <Box component="main" sx={mainSx}>
        <HeaderCard round={round} manifest={manifest} />

        {round.status === "PAUSED" && <Alert severity="info">{t("round_paused_explanation")}</Alert>}

        <Box component="section" sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography component="h2" sx={sectionTitleSx}>
              {t("your_picks")}
            </Typography>
            {isOpen && (
              <Box sx={{ display: "flex", gap: "12px" }}>
                <ButtonBase type="button" sx={ghostButtonSx} onClick={quickPick}>
                  <QuickPickIcon />
                  {t("quick_pick")}
                </ButtonBase>
                <ButtonBase type="button" sx={ghostButtonSx} onClick={() => {
                  trackCouponTool("clear", { round_id: round.id });
                  clearPicks();
                }}>
                  {t("clear")}
                </ButtonBase>
              </Box>
            )}
          </Box>
          <ProgressCells cells={picks.map((signs) => (signs.length > 0 ? { background: "var(--sign-background)" } : {}))} />
          <Typography sx={{ ...supportiveSx, fontSize: 14 } as SxProps<Theme>}>
            {t("markets_picked", { done: numPicked, total: numLegs })} · {t("multi_pick_hint")}
          </Typography>
          {hasShares && <Typography sx={{ ...supportiveSx, fontSize: 14 } as SxProps<Theme>}>{t("rows_spread_hint")}</Typography>}
        </Box>

        {manifest.markets.map((market) => {
          const signs = marketSigns(market);
          const picked = picks[market.pos] ?? [];
          const shares = hasShares ? rowShares(round.fixtures[market.pos]) : undefined;
          return (
            <Box key={market.pos} component="section" sx={marketCardSx}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                <Typography sx={{ fontFamily: "var(--font-body)", fontSize: 16, fontWeight: 600, color: "var(--thegame-accent, inherit)" }}>{getMarketLabel(market, t)}</Typography>
                <PickTag count={picked.length} />
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${signs.length}, minmax(0, 1fr))`, gap: "8px" }}>
                {signs.map((sign) => {
                  const couponSign = toCouponSign(sign);
                  return (
                    <TheGamePill
                      key={sign}
                      label={getSelectionLabel(market, sign, t)}
                      mine={picked.includes(couponSign)}
                      share={shares ? shares[couponSign] ?? 0 : undefined}
                      disabled={!isOpen}
                      onClick={() => togglePick(market.pos, couponSign)}
                    />
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>

      {isOpen && (
        <Box
          component="section"
          sx={{
            position: "sticky",
            bottom: 0,
            background: "var(--background_4)",
            boxShadow: barShadow,
            borderRadius: "12px 12px 0 0",
            padding: "14px 16px 18px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
            <Typography sx={{ ...supportiveSx, fontSize: 14 } as SxProps<Theme>}>{t("stake")}</Typography>
            <Box sx={{ display: "flex", gap: "6px" }}>
              {STAKE_MULTIPLIERS.map((m) => (
                <ButtonBase
                  key={m}
                  type="button"
                  aria-pressed={stakeMultiplier === m}
                  sx={{ ...chipSx, ...(stakeMultiplier === m ? chipOnSx : {}) } as SxProps<Theme>}
                  onClick={() => {
                    trackCouponTool("stake_multiplier", { stake_multiplier: m });
                    setStakeMultiplier(m);
                  }}
                >
                  ×{m}
                </ButtonBase>
              ))}
            </Box>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "8px", borderTop: `1px solid ${LINE}`, paddingTop: "10px" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography sx={supportiveSx}>{t("number_of_rows")}</Typography>
              <Typography sx={{ ...statValueSx, fontSize: 18 } as SxProps<Theme>}>{isComplete ? numRows : "–"}</Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography sx={supportiveSx}>{t("row_price")}</Typography>
              <Typography sx={{ ...statValueSx, fontSize: 18 } as SxProps<Theme>}>{nf.format(rowPrice)}</Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
              <Typography sx={supportiveSx}>{t("total_cost")}</Typography>
              <Typography sx={{ ...statValueSx, fontSize: 18 } as SxProps<Theme>}>{isComplete ? nf.format(total) : "–"}</Typography>
            </Box>
          </Box>
          <ButtonBase
            type="button"
            sx={mainButtonSx}
            disabled={!isComplete || (!isRealMode && !showLogin)}
            onClick={handlePayClick}
          >
            {mainButtonText}
          </ButtonBase>
        </Box>
      )}
    </Box>
  );
}
